import { DateTime, Duration } from "luxon"

import { escapeRegExp } from "../../../../lib/stringLib"
import type { DateTimeFactory } from "../../../localization/useDateTime"

import {
    ALL_FLOORS,
    MeetingRoomBookingState,
    MeetingRoomFreeLineKind,
    MeetingRoomStatus,
    MeetingRoomStatusFilter,
    MeetingRoomsView,
} from "./meetingRoomsTypes"
import type {
    HourOccupancy,
    MeetingRoomTimelineSegment,
    MeetingRoomBookingItem,
    MeetingRoomExportRow,
    MeetingRoomHourGroup,
    MeetingRoomRoomGroup,
    MeetingRoomBooking,
    MeetingRoomBusyInterval,
    MeetingRoomFreeLine,
    MeetingRoomHourRange,
    MeetingRoomRow,
    MeetingRoomsOverview,
    MeetingRoomStatusCounts,
    MeetingRoomTablePagination,
    MeetingRoomTableRow,
    ResourceOccupancyAvailability,
    ResourceOccupancyItem,
    ResourceOccupancyOrder,
    ResourceOccupancyResponse,
} from "./meetingRoomsTypes"

export const OVERVIEW_FIRST_HOUR = 7
export const OVERVIEW_LAST_HOUR = 20

const FREE_SLOT = "0"

const API_ZONE = "Europe/Copenhagen"

export const DEFAULT_HOUR_RANGE: MeetingRoomHourRange = {
    firstHour: OVERVIEW_FIRST_HOUR,
    lastHour: OVERVIEW_LAST_HOUR,
}

function toHours({ firstHour, lastHour }: MeetingRoomHourRange): ReadonlyArray<number> {
    return Array.from({ length: lastHour - firstHour + 1 }, (_, index) => firstHour + index)
}

// Meeting times come as SQL and availability as ISO without an offset; both are Copenhagen time. parseDateTimeFromApi
// reads offset-less ISO in the browser zone, which would shift the busy slots against the meetings.
function toMillis(value: string | undefined) {
    if (!value) return undefined
    const sqlDateTime = DateTime.fromSQL(value, { zone: API_ZONE })
    const dateTime = sqlDateTime.isValid ? sqlDateTime : DateTime.fromISO(value, { zone: API_ZONE })
    return dateTime.isValid ? dateTime.toMillis() : undefined
}

export function isOverCapacity(booking: MeetingRoomBooking, room: MeetingRoomRow) {
    return booking.participants !== undefined && room.seats !== undefined && booking.participants > room.seats
}

// availabilityView splits [startTime, endTime) into equal slots; any non-"0" slot is busy.
function toBusyIntervals(
    availability: ResourceOccupancyAvailability,
    slotMinutes: number | undefined,
): MeetingRoomBusyInterval[] {
    if (!availability.availabilityView) return []

    const startMillis = toMillis(availability.startTime)
    const endMillis = toMillis(availability.endTime)
    if (startMillis === undefined || endMillis === undefined || endMillis <= startMillis) return []

    const slotMillis = slotMinutes
        ? Duration.fromObject({ minutes: slotMinutes }).toMillis()
        : (endMillis - startMillis) / availability.availabilityView.length

    return Array.from(availability.availabilityView).reduce<MeetingRoomBusyInterval[]>((intervals, slot, index) => {
        if (slot === FREE_SLOT) return intervals

        const slotStart = startMillis + index * slotMillis
        const slotEnd = slotStart + slotMillis
        const last = intervals[intervals.length - 1]

        if (last && last.endMillis === slotStart) {
            return [...intervals.slice(0, -1), { startMillis: last.startMillis, endMillis: slotEnd }]
        }

        return [...intervals, { startMillis: slotStart, endMillis: slotEnd }]
    }, [])
}

function toBookingWindow(startDateTime?: string, endDateTime?: string): MeetingRoomBusyInterval | undefined {
    const startMillis = toMillis(startDateTime)
    const endMillis = toMillis(endDateTime)
    return startMillis !== undefined && endMillis !== undefined ? { startMillis, endMillis } : undefined
}

function toBooking(order: ResourceOccupancyOrder, roomId: number): MeetingRoomBooking {
    const roomResource = order.bookingDetails?.resources?.find((resource) => resource.id === roomId)

    return {
        orderId: order.id,
        isCheckedIn: order.checkIn ? (order.checkIn.isValid ?? Boolean(order.checkIn.checkInTime)) : undefined,
        meetingName: order.meetingDetails?.meetingName,
        startDateTime: order.meetingDetails?.startDateTime,
        endDateTime: order.meetingDetails?.endDateTime,
        timeRange: toBookingWindow(order.meetingDetails?.startDateTime, order.meetingDetails?.endDateTime),
        organizers: order.organizers ?? [],
        participants: order.orderSummary?.participants,
        hasCatering: order.orderSummary?.hasCatering,
        hasBooking: order.orderSummary?.hasBooking,
        hasAudioVideoEquipment: order.orderSummary?.hasAudioVideoEquipment,
        tableSetupName: roomResource?.room?.tableSetup?.name,
        isPrivate: order.meetingDetails?.isPrivate,
        isExternal: order.meetingDetails?.isExternal,
    }
}

function toBookingIntervals(bookings: ReadonlyArray<MeetingRoomBooking>): MeetingRoomBusyInterval[] {
    return bookings.flatMap(({ timeRange }) => (timeRange ? [timeRange] : []))
}

function overlaps(interval: MeetingRoomBusyInterval, timeRange: MeetingRoomBusyInterval) {
    return interval.startMillis < timeRange.endMillis && interval.endMillis > timeRange.startMillis
}

function subtractWindows(
    interval: MeetingRoomBusyInterval,
    windows: ReadonlyArray<MeetingRoomBusyInterval>,
): MeetingRoomBusyInterval[] {
    return windows.reduce<MeetingRoomBusyInterval[]>(
        (pieces, timeRange) =>
            pieces.flatMap((piece) =>
                overlaps(piece, timeRange)
                    ? [
                          { startMillis: piece.startMillis, endMillis: timeRange.startMillis },
                          { startMillis: timeRange.endMillis, endMillis: piece.endMillis },
                      ].filter(({ startMillis, endMillis }) => endMillis > startMillis)
                    : [piece],
            ),
        [interval],
    )
}

function toUnbookedBusyIntervals(
    busyIntervals: ReadonlyArray<MeetingRoomBusyInterval>,
    bookingIntervals: ReadonlyArray<MeetingRoomBusyInterval>,
    bufferMinutes: number,
): MeetingRoomBusyInterval[] {
    const bufferMillis = Duration.fromObject({ minutes: bufferMinutes }).toMillis()
    const bufferedBookings = bookingIntervals.map(({ startMillis, endMillis }) => ({
        startMillis: startMillis - bufferMillis,
        endMillis: endMillis + bufferMillis,
    }))
    return busyIntervals.flatMap((interval) => subtractWindows(interval, bufferedBookings))
}

function toRoomRow(
    { resource, availabilities, orders }: ResourceOccupancyItem,
    slotMinutes: number | undefined,
): MeetingRoomRow {
    const bookings = (orders ?? []).map((order) => toBooking(order, resource.id))
    const availabilityIntervals = (availabilities ?? []).flatMap((availability) =>
        toBusyIntervals(availability, slotMinutes),
    )
    const bookingIntervals = toBookingIntervals(bookings)

    return {
        id: resource.id,
        name: resource.name ?? "",
        floorName: resource.room?.floor?.name ?? "",
        seats: resource.room?.seats,
        equipment: (resource.room?.avEquipments ?? []).map((equipment) => equipment.name),
        isOutOfService: Boolean(resource.outOfService?.active),
        outOfServiceMessage: resource.outOfService?.message,
        busyIntervals: [...availabilityIntervals, ...bookingIntervals],
        unbookedBusyIntervals: toUnbookedBusyIntervals(availabilityIntervals, bookingIntervals, resource.buffer ?? 0),
        bookings,
    }
}

export function selectMeetingRoomsOverview(data: ResourceOccupancyResponse): MeetingRoomsOverview {
    const slotMinutes = data.availabilityDetails?.availabilityViewInterval
    const rooms = data.resources.map((item) => toRoomRow(item, slotMinutes))
    return {
        summaryMessages: data.summary?.messages ?? [],
        rooms,
        floorNames: getFloorNames(rooms),
    }
}

export function toHourWindow(date: DateTime, hour: number): MeetingRoomBusyInterval {
    const start = date.startOf("day").set({ hour })
    return { startMillis: start.toMillis(), endMillis: start.plus({ hours: 1 }).toMillis() }
}

function getOccupiedIntervals(room: MeetingRoomRow): ReadonlyArray<MeetingRoomBusyInterval> {
    return [...toBookingIntervals(room.bookings), ...room.unbookedBusyIntervals]
}

function isRoomBusyInWindow(room: MeetingRoomRow, timeRange: MeetingRoomBusyInterval) {
    return getOccupiedIntervals(room).some((interval) => overlaps(interval, timeRange))
}

function getRoomStatus(
    room: MeetingRoomRow,
    timeRange: MeetingRoomBusyInterval,
    booking: MeetingRoomBooking | undefined,
    nowMillis: number,
): MeetingRoomStatus {
    if (room.isOutOfService) return MeetingRoomStatus.OUT_OF_SERVICE
    const hasStarted = booking?.timeRange !== undefined && booking.timeRange.startMillis <= nowMillis
    if (hasStarted && booking?.isCheckedIn === false) return MeetingRoomStatus.NOT_CHECKED_IN
    return isRoomBusyInWindow(room, timeRange) ? MeetingRoomStatus.BOOKED : MeetingRoomStatus.AVAILABLE
}

function isRoomBooked(status: MeetingRoomStatus) {
    return status === MeetingRoomStatus.BOOKED || status === MeetingRoomStatus.NOT_CHECKED_IN
}

function getBookingInWindow(room: MeetingRoomRow, timeRange: MeetingRoomBusyInterval): MeetingRoomBooking | undefined {
    return room.bookings.find((booking) => booking.timeRange && overlaps(booking.timeRange, timeRange))
}

function getFreeWindows(
    room: MeetingRoomRow,
    fromMillis: number,
    dayEndMillis: number,
): ReadonlyArray<MeetingRoomBusyInterval> {
    const busy = room.busyIntervals
        .filter((interval) => interval.endMillis > fromMillis)
        .sort((first, second) => first.startMillis - second.startMillis)

    const { windows, cursor } = busy.reduce<{ windows: MeetingRoomBusyInterval[]; cursor: number }>(
        (acc, interval) => ({
            windows:
                interval.startMillis > acc.cursor
                    ? [...acc.windows, { startMillis: acc.cursor, endMillis: interval.startMillis }]
                    : acc.windows,
            cursor: Math.max(acc.cursor, interval.endMillis),
        }),
        { windows: [], cursor: fromMillis },
    )

    return cursor < dayEndMillis ? [...windows, { startMillis: cursor, endMillis: dayEndMillis }] : windows
}

function isRoomBusyAt(room: MeetingRoomRow, millis: number) {
    return getOccupiedIntervals(room).some((interval) => interval.startMillis <= millis && interval.endMillis > millis)
}

function getFloorNames(rooms: ReadonlyArray<MeetingRoomRow>): ReadonlyArray<string> {
    return Array.from(new Set(rooms.map((room) => room.floorName).filter(Boolean)))
}

function matchesStatusFilter(status: MeetingRoomStatus, filter: MeetingRoomStatusFilter) {
    switch (filter) {
        case MeetingRoomStatusFilter.ALL:
            return true
        case MeetingRoomStatusFilter.BOOKED:
            return isRoomBooked(status)
        case MeetingRoomStatusFilter.AVAILABLE:
            return status === MeetingRoomStatus.AVAILABLE
    }
}

export function formatTimeWindow(
    dateTimeFactory: DateTimeFactory,
    { startMillis, endMillis }: MeetingRoomBusyInterval,
): string {
    const start = dateTimeFactory.formatTime(dateTimeFactory.fromMillis(startMillis))
    const end = dateTimeFactory.formatTime(dateTimeFactory.fromMillis(endMillis))
    return `${start}–${end}`
}

const HOUR_FORMAT = "HH"

export const TABLE_PAGE_SIZE_OPTIONS = [10, 50, 100, 1000]

export const DEFAULT_TABLE_PAGE_SIZE = 100

export function getTablePagination(
    rowCount: number,
    requestedPageIndex: number,
    pageSize: number,
): MeetingRoomTablePagination {
    const pageCount = Math.max(1, Math.ceil(rowCount / pageSize))
    return { pageIndex: Math.min(requestedPageIndex, pageCount - 1), pageCount, pageSize }
}

export function formatHourRange(
    dateTimeFactory: DateTimeFactory,
    { startMillis, endMillis }: MeetingRoomBusyInterval,
): string {
    const start = dateTimeFactory.formatCustom(dateTimeFactory.fromMillis(startMillis), HOUR_FORMAT)
    const end = dateTimeFactory.formatCustom(dateTimeFactory.fromMillis(endMillis), HOUR_FORMAT)
    return `${start}–${end}`
}

export function getDayEndMillis(date: DateTime, { lastHour }: MeetingRoomHourRange) {
    return toHourWindow(date, lastHour).endMillis
}

export function getOverviewHourRange(rooms: ReadonlyArray<MeetingRoomRow>, date: DateTime): MeetingRoomHourRange {
    const day = {
        startMillis: date.startOf("day").toMillis(),
        endMillis: date.startOf("day").plus({ days: 1 }).toMillis(),
    }
    const toHour = (millis: number) => DateTime.fromMillis(millis, { zone: date.zone }).hour

    return rooms
        .flatMap(getOccupiedIntervals)
        .filter((interval) => overlaps(interval, day))
        .reduce<MeetingRoomHourRange>(
            (range, { startMillis, endMillis }) => ({
                firstHour: Math.min(range.firstHour, toHour(Math.max(startMillis, day.startMillis))),
                lastHour: Math.max(range.lastHour, toHour(Math.min(endMillis, day.endMillis) - 1)),
            }),
            DEFAULT_HOUR_RANGE,
        )
}

export function clampHour(hour: number, { firstHour, lastHour }: MeetingRoomHourRange) {
    return Math.min(Math.max(hour, firstHour), lastHour)
}

export function buildHourOccupancy(
    rooms: ReadonlyArray<MeetingRoomRow>,
    date: DateTime,
    hourRange: MeetingRoomHourRange,
): ReadonlyArray<HourOccupancy> {
    return toHours(hourRange).map((hour) => {
        const timeRange = toHourWindow(date, hour)
        return { hour, timeRange, bookedCount: rooms.filter((room) => isRoomBusyInWindow(room, timeRange)).length }
    })
}

function toFreeLine(
    status: MeetingRoomStatus,
    first: MeetingRoomBusyInterval | undefined,
    dayEndMillis: number,
): MeetingRoomFreeLine {
    if (status === MeetingRoomStatus.OUT_OF_SERVICE) return { kind: MeetingRoomFreeLineKind.OUT_OF_SERVICE }

    const isBooked = isRoomBooked(status)
    if (!first) {
        return {
            kind: isBooked ? MeetingRoomFreeLineKind.BOOKED_REST_OF_DAY : MeetingRoomFreeLineKind.NO_FREE_TIME,
        }
    }

    const reachesDayEnd = first.endMillis >= dayEndMillis
    if (isBooked) {
        return {
            kind: reachesDayEnd ? MeetingRoomFreeLineKind.FREE_FROM : MeetingRoomFreeLineKind.FREE_BETWEEN,
            timeRange: first,
        }
    }

    return {
        kind: reachesDayEnd ? MeetingRoomFreeLineKind.FREE_REST_OF_DAY : MeetingRoomFreeLineKind.FREE_UNTIL,
        timeRange: first,
    }
}

export function buildRoomsAtHour(
    rooms: ReadonlyArray<MeetingRoomRow>,
    hourWindow: MeetingRoomBusyInterval,
    dayEndMillis: number,
    nowMillis: number,
    isToday: boolean,
): ReadonlyArray<MeetingRoomTableRow> {
    return rooms.map((room) => {
        const booking = getBookingInWindow(room, hourWindow)
        const status = getRoomStatus(room, hourWindow, booking, nowMillis)
        const [first, ...laterWindows] = getFreeWindows(room, hourWindow.startMillis, dayEndMillis)
        const isInUseNow = isToday && isRoomBusyAt(room, nowMillis)
        const liveBooking = isInUseNow
            ? room.bookings.find(
                  ({ timeRange }) => timeRange && timeRange.startMillis <= nowMillis && timeRange.endMillis > nowMillis,
              )
            : undefined

        return {
            room,
            status,
            isBookable:
                status === MeetingRoomStatus.AVAILABLE &&
                !room.busyIntervals.some((interval) => overlaps(interval, hourWindow)),
            booking,
            isInUseNow,
            liveBooking,
            freeLine: toFreeLine(status, first, dayEndMillis),
            laterWindows,
        }
    })
}

export function countStatuses(rows: ReadonlyArray<MeetingRoomTableRow>): MeetingRoomStatusCounts {
    return rows.reduce<MeetingRoomStatusCounts>(
        (counts, { status }) => ({
            ...counts,
            [MeetingRoomStatusFilter.BOOKED]: counts[MeetingRoomStatusFilter.BOOKED] + Number(isRoomBooked(status)),
            [MeetingRoomStatusFilter.AVAILABLE]:
                counts[MeetingRoomStatusFilter.AVAILABLE] + Number(status === MeetingRoomStatus.AVAILABLE),
        }),
        {
            [MeetingRoomStatusFilter.ALL]: rows.length,
            [MeetingRoomStatusFilter.BOOKED]: 0,
            [MeetingRoomStatusFilter.AVAILABLE]: 0,
        },
    )
}

type MeetingRoomFilters = Readonly<{
    statusFilter: MeetingRoomStatusFilter
    floor: string
    search: string
}>

// Grouped views list the whole day, so search matches any booking of the room, not only the one at the selected hour.
export function filterRowsForDay(
    rows: ReadonlyArray<MeetingRoomTableRow>,
    { statusFilter, floor, search }: MeetingRoomFilters,
): ReadonlyArray<MeetingRoomTableRow> {
    return rows.filter(
        ({ room, status }) =>
            matchesStatusFilter(status, statusFilter) &&
            (floor === ALL_FLOORS || room.floorName === floor) &&
            matchesSearch(room, room.bookings, search),
    )
}

export function filterRows(
    rows: ReadonlyArray<MeetingRoomTableRow>,
    { statusFilter, floor, search }: MeetingRoomFilters,
): ReadonlyArray<MeetingRoomTableRow> {
    return rows.filter(
        ({ room, status, booking }) =>
            matchesStatusFilter(status, statusFilter) &&
            (floor === ALL_FLOORS || room.floorName === floor) &&
            matchesSearch(room, booking ? [booking] : [], search),
    )
}

export function toSearchRegex(search: string): RegExp | undefined {
    const query = search.trim()
    return query ? new RegExp(`(${escapeRegExp(query)})`, "i") : undefined
}

function matchesSearch(room: MeetingRoomRow, bookings: ReadonlyArray<MeetingRoomBooking>, search: string) {
    const query = search.trim().toLowerCase()
    if (!query) return true

    const haystack = [
        room.name,
        ...bookings.flatMap((booking) => [
            booking.meetingName,
            ...booking.organizers.flatMap((organizer) => [organizer.name, organizer.department?.name]),
        ]),
    ]
    return haystack.some((value) => value?.toLowerCase().includes(query))
}

function toBookingState(timeRange: MeetingRoomBusyInterval, nowMillis: number): MeetingRoomBookingState {
    if (timeRange.endMillis <= nowMillis) return MeetingRoomBookingState.FINISHED
    return timeRange.startMillis <= nowMillis ? MeetingRoomBookingState.IN_PROGRESS : MeetingRoomBookingState.UPCOMING
}

export function getRoomBookingItems(room: MeetingRoomRow, nowMillis: number): ReadonlyArray<MeetingRoomBookingItem> {
    return room.bookings
        .flatMap((booking) =>
            booking.timeRange
                ? [{ room, booking, timeRange: booking.timeRange, state: toBookingState(booking.timeRange, nowMillis) }]
                : [],
        )
        .sort((first, second) => first.timeRange.startMillis - second.timeRange.startMillis)
}

export function buildHourGroups(
    rows: ReadonlyArray<MeetingRoomTableRow>,
    date: DateTime,
    hourRange: MeetingRoomHourRange,
    nowMillis: number,
): ReadonlyArray<MeetingRoomHourGroup> {
    const items = rows.flatMap(({ room }) => getRoomBookingItems(room, nowMillis))
    return toHours(hourRange).map((hour) => {
        const timeRange = toHourWindow(date, hour)
        return { hour, timeRange, items: items.filter((item) => overlaps(item.timeRange, timeRange)), freeRooms: [] }
    })
}

// The whole day is listed, so free rooms come from every room matching floor and search, not only the ones
// free at the selected hour.
export function buildFreeHourGroups(
    rows: ReadonlyArray<MeetingRoomTableRow>,
    date: DateTime,
    hourRange: MeetingRoomHourRange,
    { floor, search }: Pick<MeetingRoomFilters, "floor" | "search">,
): ReadonlyArray<MeetingRoomHourGroup> {
    const rooms = rows
        .map(({ room }) => room)
        .filter(
            (room) =>
                !room.isOutOfService &&
                (floor === ALL_FLOORS || room.floorName === floor) &&
                matchesSearch(room, [], search),
        )
    return toHours(hourRange).map((hour) => {
        const timeRange = toHourWindow(date, hour)
        return { hour, timeRange, items: [], freeRooms: rooms.filter((room) => !isRoomBusyInWindow(room, timeRange)) }
    })
}

export function buildRoomGroups(
    rows: ReadonlyArray<MeetingRoomTableRow>,
    nowMillis: number,
): ReadonlyArray<MeetingRoomRoomGroup> {
    return rows.map(({ room }) => ({ room, items: getRoomBookingItems(room, nowMillis) }))
}

type MeetingRoomViewRows = Readonly<{
    tableRows: ReadonlyArray<MeetingRoomTableRow>
    hourGroups: ReadonlyArray<MeetingRoomHourGroup>
    roomGroups: ReadonlyArray<MeetingRoomRoomGroup>
}>

export function buildExportRows(
    view: MeetingRoomsView,
    { tableRows, hourGroups, roomGroups }: MeetingRoomViewRows,
    nowMillis: number,
): ReadonlyArray<MeetingRoomExportRow> {
    switch (view) {
        case MeetingRoomsView.STATUS:
            return tableRows.map(({ room, booking, status }) => ({
                room,
                status,
                item: getRoomBookingItems(room, nowMillis).find((item) => item.booking === booking),
            }))
        case MeetingRoomsView.BY_HOUR:
            return [
                ...Array.from(new Set(hourGroups.flatMap(({ items }) => items)), (item) => ({ room: item.room, item })),
                ...Array.from(new Set(hourGroups.flatMap(({ freeRooms }) => freeRooms)), (room) => ({ room })),
            ]
        case MeetingRoomsView.BY_ROOM:
            return roomGroups.flatMap(({ room, items }) =>
                items.length ? items.map((item) => ({ room, item })) : [{ room }],
            )
    }
}

export function getPeakHour(hours: ReadonlyArray<HourOccupancy>): HourOccupancy | undefined {
    return hours.reduce<HourOccupancy | undefined>(
        (peak, hour) => (!peak || hour.bookedCount > peak.bookedCount ? hour : peak),
        undefined,
    )
}

// Shorter gaps are rounding noise between back-to-back bookings, not bookable free time.
const MIN_FREE_SEGMENT = Duration.fromObject({ minutes: 15 }).toMillis()

export function getDayStartMillis(date: DateTime, { firstHour }: MeetingRoomHourRange) {
    return toHourWindow(date, firstHour).startMillis
}

export function buildTimelineSegments(
    items: ReadonlyArray<MeetingRoomBookingItem>,
    unbookedBusyIntervals: ReadonlyArray<MeetingRoomBusyInterval>,
    fromMillis: number,
    dayEndMillis: number,
): ReadonlyArray<MeetingRoomTimelineSegment> {
    const clip = (timeRange: MeetingRoomBusyInterval) => ({
        startMillis: Math.max(timeRange.startMillis, fromMillis),
        endMillis: Math.min(timeRange.endMillis, dayEndMillis),
    })
    const isVisible = (timeRange: MeetingRoomBusyInterval) =>
        timeRange.endMillis > fromMillis && timeRange.startMillis < dayEndMillis

    const occupiedSegments: MeetingRoomTimelineSegment[] = [
        ...items
            .filter((item) => isVisible(item.timeRange))
            .map((item): MeetingRoomTimelineSegment => {
                const timeRange = clip(item.timeRange)
                return { timeRange, item: { ...item, timeRange }, isOccupied: true }
            }),
        ...unbookedBusyIntervals
            .filter(isVisible)
            .map((timeRange): MeetingRoomTimelineSegment => ({ timeRange: clip(timeRange), isOccupied: true })),
    ].sort((first, second) => first.timeRange.startMillis - second.timeRange.startMillis)

    const { segments, cursor } = occupiedSegments.reduce<{ segments: MeetingRoomTimelineSegment[]; cursor: number }>(
        (acc, segment) => {
            const gapEnd = Math.max(acc.cursor, segment.timeRange.startMillis)
            const gap =
                gapEnd - acc.cursor >= MIN_FREE_SEGMENT
                    ? [{ timeRange: { startMillis: acc.cursor, endMillis: gapEnd }, isOccupied: false }]
                    : []
            return {
                segments: [...acc.segments, ...gap, segment],
                cursor: Math.max(acc.cursor, segment.timeRange.endMillis),
            }
        },
        { segments: [], cursor: fromMillis },
    )

    return dayEndMillis - cursor >= MIN_FREE_SEGMENT
        ? [...segments, { timeRange: { startMillis: cursor, endMillis: dayEndMillis }, isOccupied: false }]
        : segments
}
